import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import {
  AdminListCard,
  PageTabs,
  AdminListTable,
  ConfirmDeleteModal,
  useBatchSelection,
  type BatchAction,
} from "@components/CatalogIndex";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useLobApprovalsList } from "../api/useLobApprovalsList";
import { useLobApprovalMutations } from "../api/useLobApprovalMutations";
import { useApprovalLevelsList } from "../api/useApprovalLevelsList";
import { useApprovalLevelMutations } from "../api/useApprovalLevelMutations";
import { lobApprovalColumns } from "./columns";
import { businessApprovalColumns } from "../BusinessApprovalsSection/columns";
import { SalesApprovalSection } from "../SalesApprovalSection/SalesApprovalSection";

type ApprovalTab = "technical" | "sales" | "business";

export interface ListProps {
  // Default-active tab when the list mounts. `/admin/lob_approvals` mounts
  // with "technical" (LobApprovals are the legacy entry); `/admin/approval_levels`
  // mounts with "business" since that's the canonical replacement path.
  defaultTab?: ApprovalTab;
}

export const List = ({ defaultTab = "technical" }: ListProps = {}) => {
  const navigate = useNavigate();

  // Technical (LobApproval) tab data
  const {
    rows: techRows,
    isLoading: techLoading,
    isFetching: techFetching,
  } = useLobApprovalsList();
  const { destroy: destroyLobApproval } = useLobApprovalMutations();

  // Business (ApprovalLevel kind=business) tab data
  const {
    rows: bizRows,
    isFetching: bizFetching,
    refetch: refetchBiz,
  } = useApprovalLevelsList({ kind: "business" });
  const { destroy: destroyBiz, move: moveBiz } = useApprovalLevelMutations();

  const [tab, setTab] = useState<ApprovalTab>(defaultTab);
  const techSel = useBatchSelection([tab]);
  const bizSel = useBatchSelection([tab]);
  const [confirmTechOpen, setConfirmTechOpen] = useState(false);
  const [confirmBizOpen, setConfirmBizOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "error";
    text: string;
  } | null>(null);

  if (techLoading) return <ScopeStackSpinner />;

  const handleConfirmTech = async () => {
    const ids = [...techSel.selectedIds];
    let failures = 0;
    for (const id of ids) {
      try {
        await destroyLobApproval(id);
      } catch {
        failures += 1;
      }
    }
    setToast(
      failures === 0
        ? { variant: "success", text: `${ids.length} approval(s) removed.` }
        : {
            variant: "error",
            text: `Failed to remove ${failures} of ${ids.length} approvals.`,
          }
    );
    techSel.clear();
    setConfirmTechOpen(false);
  };

  const handleConfirmBiz = async () => {
    const ids = [...bizSel.selectedIds];
    let failures = 0;
    for (const id of ids) {
      try {
        await destroyBiz(id);
      } catch {
        failures += 1;
      }
    }
    setToast(
      failures === 0
        ? {
            variant: "success",
            text: `${ids.length} business approval(s) removed.`,
          }
        : {
            variant: "error",
            text: `Failed to remove ${failures} of ${ids.length} business approvals.`,
          }
    );
    bizSel.clear();
    setConfirmBizOpen(false);
    refetchBiz();
  };

  const handleBizReorder = async (
    moves: Array<{ id: string | number; position: number }>
  ) => {
    try {
      for (const { id, position } of moves) {
        await moveBiz(String(id), position);
      }
      refetchBiz();
    } catch {
      setToast({
        variant: "error",
        text: "Failed to reorder. The list has been refreshed.",
      });
      refetchBiz();
    }
  };

  const techActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Delete",
      onClick: () => setConfirmTechOpen(true),
      variant: "danger",
    },
  ];

  const bizActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Delete",
      onClick: () => setConfirmBizOpen(true),
      variant: "danger",
    },
  ];

  const activeBatchActions =
    tab === "technical"
      ? techActions
      : tab === "business"
      ? bizActions
      : undefined;

  const activeSelectedCount =
    tab === "technical"
      ? techSel.selectedIds.length
      : tab === "business"
      ? bizSel.selectedIds.length
      : 0;

  // The New button label + handler swap per tab so Business gets its own
  // primary action without colliding with the LOB Approval action on Technical.
  // Absolute targets so the buttons work the same regardless of whether the
  // user landed on /admin/lob_approvals or /admin/approval_levels and then
  // switched tabs to the opposite resource.
  const onNewForTab =
    tab === "technical"
      ? () => navigate("/admin/lob_approvals/new")
      : tab === "business"
      ? () => navigate("/admin/approval_levels/new")
      : undefined;

  const newLabelForTab =
    tab === "technical"
      ? "+ LOB Approval"
      : tab === "business"
      ? "+ Add Business Approval"
      : undefined;

  return (
    <>
      <AdminListCard
        title="Project Approvals"
        onNew={onNewForTab}
        newButtonLabel={newLabelForTab}
        batchActions={activeBatchActions}
        selectedCount={activeSelectedCount}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => setTab(key as ApprovalTab)}
          tabs={[
            {
              key: "technical",
              label: "Technical Approval",
              count: techRows.length,
            },
            { key: "sales", label: "Sales Approval" },
            {
              key: "business",
              label: "Business Approval",
              count: bizRows.length,
            },
          ]}
        />
        {tab === "technical" && (
          <AdminListTable
            columns={lobApprovalColumns}
            data={techRows}
            isLoading={techFetching}
            selectedIds={techSel.selectedIds}
            setSelectedIds={techSel.setSelectedIds}
            draggable={false}
            onEditHref={(row) => `/admin/lob_approvals/${row.id}/edit`}
            emptyMessage="No approvals defined."
          />
        )}
        {tab === "sales" && <SalesApprovalSection />}
        {tab === "business" && (
          <AdminListTable
            columns={businessApprovalColumns}
            data={bizRows}
            isLoading={bizFetching}
            selectedIds={bizSel.selectedIds}
            setSelectedIds={bizSel.setSelectedIds}
            draggable
            onReorder={handleBizReorder}
            onEditHref={(row) => `/admin/approval_levels/${row.id}/edit`}
            emptyMessage="No business approvals defined yet."
          />
        )}
      </AdminListCard>
      <ConfirmDeleteModal
        open={confirmTechOpen}
        title={`Permanently delete approval? (${techSel.selectedIds.length})`}
        message={`This permanently deletes ${techSel.selectedIds.length} approval rule(s). Continue?`}
        confirmLabel="Permanently Delete"
        onConfirm={handleConfirmTech}
        onCancel={() => setConfirmTechOpen(false)}
      />
      <ConfirmDeleteModal
        open={confirmBizOpen}
        title={`Delete business approval? (${bizSel.selectedIds.length})`}
        message={`Delete ${bizSel.selectedIds.length} business approval(s)?`}
        onConfirm={handleConfirmBiz}
        onCancel={() => setConfirmBizOpen(false)}
      />
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
